import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, MoveHorizontalIcon } from 'lucide-react';
import { materialSlides } from '../data/content';
const AUTOPLAY_MS = 6000;
const NAV_BUTTON_CLASS = 'absolute top-1/2 z-10 flex h-10 w-10 shrink-0 -translate-y-1/2 items-center justify-center rounded-full bg-inverse/75 text-primary-foreground backdrop-blur transition-colors duration-150 ease-out-strong hover:bg-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:static sm:h-11 sm:w-11 sm:translate-y-0 sm:bg-primary sm:hover:bg-primary-hover';
export function MaterialCarousel() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const total = materialSlides.length;
  const slide = materialSlides[index];
  const goTo = useCallback((next: number, manual = true) => {
    setDirection(next > index || index === total - 1 && next >= total ? 1 : -1);
    setIndex((next + total) % total);
    if (manual) setIsPaused(true);
  }, [index, total]);
  useEffect(() => {
    if (isPaused) return;
    const id = window.setTimeout(() => goTo(index + 1, false), AUTOPLAY_MS);
    return () => window.clearTimeout(id);
  }, [index, isPaused, goTo]);
  return <section id="materiale" className="w-full overflow-hidden bg-inverse-deep px-3 py-20 sm:px-5 sm:py-28" data-id="element-142" data-mp="35d4yttra6s52">
      <div className="mx-auto max-w-5xl" data-id="element-143" data-mp="30etolhx2qfmb">
        <div className="text-center" data-id="element-144" data-mp="30dsb76sl5h1f">
          <span className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.2em] text-primary-foreground shadow-cta" data-id="element-145" data-mp="3edjqdauu93h0">
            Aperçu du pack
          </span>
          <h2 className="mt-5 font-display text-[1.9rem] font-black leading-tight tracking-tight text-surface sm:text-[2.8rem]" data-id="element-146" data-mp="1k0tw2s0k5s93">
            Regardez le matériel{' '}
            <span className="italic text-primary-soft" data-id="element-147" data-mp="3f36zc8nevp8s">de l&apos;intérieur</span>
          </h2>
          <p className="mx-auto mt-4 max-w-lg text-sm leading-relaxed text-surface/65 sm:text-base" data-id="element-148" data-mp="3k1rzy96ksaxr">
            De vraies pages des volumes&nbsp;: théorie visuelle, activités et
            fiches déjà mises en page, prêtes à être imprimées ou projetées en
            classe.
          </p>
        </div>

        <div className="mt-12 grid items-center gap-10 md:grid-cols-[minmax(0,1fr)_280px]" data-id="element-149" data-mp="1wyiw6i3fw0e">
          <div className="relative mx-auto flex w-full max-w-[700px] items-center gap-0 sm:gap-5" data-id="element-150" data-mp="6v8genraj8p6">
            <button type="button" onClick={() => goTo(index - 1)} aria-label="Page précédente" className={`left-1 ${NAV_BUTTON_CLASS}`} data-id="element-151" data-mp="bmi3phpd9zxw">
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-152" data-mp="lkxufjkhf76v" />
            </button>

            <div className="relative flex-1" data-id="element-153" data-mp="plq2walj5823">
              <div className="relative aspect-[3/4] overflow-hidden rounded-3xl border-4 border-primary/70 bg-surface shadow-float" data-id="element-154" data-mp="pmj12n6fap8m">
                <AnimatePresence initial={false} mode="wait" data-id="element-155" data-mp="262ggfwwqerg6">
                  <motion.img key={slide.src} src={slide.src} alt={`Page du matériel : ${slide.title}`} drag="x" dragConstraints={{
                  left: 0,
                  right: 0
                }} dragElastic={0.12} onDragEnd={(_, info) => {
                  if (info.offset.x < -60) goTo(index + 1);
                  if (info.offset.x > 60) goTo(index - 1);
                }} initial={{
                  opacity: 0,
                  x: direction * 48
                }} animate={{
                  opacity: 1,
                  x: 0
                }} exit={{
                  opacity: 0,
                  x: direction * -48
                }} transition={{
                  duration: 0.28,
                  ease: [0.23, 1, 0.32, 1]
                }} className="absolute inset-0 h-full w-full cursor-grab object-contain p-1.5 active:cursor-grabbing sm:p-2" data-id="element-156" data-mp="260vn2am5lejw" />
                </AnimatePresence>
              </div>

              <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-primary px-4 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.14em] text-primary-foreground shadow-cta" data-id="element-157" data-mp="2wbe9jf6xd3yy">
                {slide.label}
              </span>

              <div className="mt-5 h-1 w-full overflow-hidden rounded-full bg-surface/15" data-id="element-158" data-mp="2nay6u7y0a0v5">
                <motion.div key={`${index}-${isPaused}`} className="h-full rounded-full bg-primary" initial={{
                width: isPaused ? '100%' : '0%'
              }} animate={{
                width: '100%'
              }} transition={{
                duration: isPaused ? 0 : AUTOPLAY_MS / 1000,
                ease: 'linear'
              }} data-id="element-159" data-mp="3b12agfb720m5" />
              </div>
            </div>

            <button type="button" onClick={() => goTo(index + 1)} aria-label="Page suivante" className={`right-1 ${NAV_BUTTON_CLASS}`} data-id="element-160" data-mp="2kz4cdzqcjzx">
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-161" data-mp="1icku2lpzj2zu" />
            </button>
          </div>

          <div className="text-center md:text-left" aria-live="polite" data-id="element-162" data-mp="1w24pf6h8kxz4">
            <p className="font-display text-sm font-bold tabular-nums text-primary-soft" data-id="element-163" data-mp="1s1unq60h8tq2">
              {String(index + 1).padStart(2, '0')}
              <span className="text-surface/35" data-id="element-164" data-mp="14cjt769pzvkd"> / {String(total).padStart(2, '0')}</span>
            </p>
            <h3 className="mt-3 font-display text-xl font-black leading-snug text-surface sm:text-2xl" data-id="element-165" data-mp="zehz7qarabu4">
              {slide.title}
            </h3>
            <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-surface/60 md:mx-0" data-id="element-166" data-mp="18dv92k2sl2fd">
              {slide.text}
            </p>

            <ul className="mt-7 flex items-center justify-center gap-3 md:justify-start" data-id="element-167" data-mp="2oqk760rr5ue5">
              {materialSlides.map((item, itemIndex) => <li key={item.src} data-id="element-168" data-mp="1hfmdy6zmsz6l" data-mpi={itemIndex}>
                  <button type="button" onClick={() => goTo(itemIndex)} aria-label={`Aller à la page ${itemIndex + 1} : ${item.title}`} aria-current={itemIndex === index} className={`block overflow-hidden rounded-xl border-2 bg-surface transition-colors duration-150 ease-out-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${itemIndex === index ? 'border-primary' : 'border-surface/15 hover:border-surface/45'}`} data-id="element-169" data-mp="1heb17ho7kh3c">
                    <img src={item.src} alt="" className={`h-24 w-16 object-cover object-top transition-opacity duration-150 ${itemIndex === index ? 'opacity-100' : 'opacity-55'}`} data-id="element-170" data-mp="1cg9f0nvhk1nn" />
                  </button>
                </li>)}
            </ul>

            <p className="mt-6 flex items-center justify-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-surface/40 md:justify-start" data-id="element-171" data-mp="ce37stby8d9r">
              <MoveHorizontalIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-172" data-mp="2gou31heu2l5" />
              Faites glisser pour feuilleter
            </p>
          </div>
        </div>
      </div>
    </section>;
}